import type MP4Box from 'mp4box';
import type { AddError, AddWarning, VideoUploadTrackEventProperties } from '../../types/upload-video';
import { calculateGop } from '../calculateGop';
import { isVariableFrameRateMode } from '../isVariableFrameRateMode';
import { logSampleDuration } from '../logSampleDuration';
import { logTimescale } from '../logTimescale';
import { validateAspectRatio } from '../validateAspectRatio';
import { validateAudioBitrate } from '../validateAudioBitrate';
import { validateAudioType } from '../validateAudioType';
import { validateDuration } from '../validateDuration';
import { validateFileSize } from '../validateFileSize';
import { validateFps } from '../validateFps';
import { validateMaxAudioChannels } from '../validateMaxAudioChannels';
import { validateMaxResolution } from '../validateMaxResolution';
import { validateMaxVideoBitrate } from '../validateMaxVideoBitrate';
import { validateMaxVideoChannels } from '../validateMaxVideoChannels';
import validatePixelFormat from '../validatePixelFormat/validate-pixel-format';
import { validateSampleRate } from '../validateSampleRate';
import { validateUploadFileHasAudio } from '../validateUploadFileHasAudio';
import { validateVideoType } from '../validateVideoType';
import type { VideoFileToUpload } from '../../modules/videoFileToUpload';

const validateAndLogVideoFile = ({
  allow4k,
  allowMoreVideoCodecs,
  onError,
  onWarning,
  trackEventProperties,
  videoFile,
  isUniversalUpload = false,
}: {
  allow4k: boolean;
  allowMoreVideoCodecs: boolean;
  onError: AddError;
  onWarning: AddWarning;
  trackEventProperties: VideoUploadTrackEventProperties;
  videoFile: MP4Box.MP4File;
  isUniversalUpload?: boolean;
}): VideoFileToUpload.Attributes | Pick<VideoFileToUpload.Attributes, 'resolutionHeight'> => {
  const info: MP4Box.Mp4BoxInfo = videoFile.getInfo();

  if (allow4k) {
    console.log('Video up to 2160p may be uploaded');
  } else {
    console.log('Video up to 1080p may be uploaded');
  }

  const resolution = validateMaxResolution(allow4k, info, onError, trackEventProperties);
  if (isUniversalUpload) {
    console.log('Using universal upload');
    return {
      resolutionHeight: resolution.resolutionHeight,
    };
  }

  const gopSeconds: number | null = calculateGop({
    mp4BoxFile: videoFile,
    info,
    addError: onError,
    addWarning: onWarning,
    trackEventProperties,
  });
  const isVariableFramerate = isVariableFrameRateMode(videoFile, info);
  const framerate: string = isVariableFramerate ? 'variable' : validateFps(info, onError, trackEventProperties);
  validateUploadFileHasAudio(info, onError);
  const videoBitrateKbps: number = validateMaxVideoBitrate(info, onError, trackEventProperties);
  validateMaxVideoChannels(info, onError, trackEventProperties);
  const audioBitrateKbps = validateAudioBitrate(info, onError, trackEventProperties);
  const audioSampleRate: number = validateSampleRate(videoFile, info, onError, trackEventProperties);
  validateMaxAudioChannels(info, onError, trackEventProperties);
  validateAudioType(info, onError);
  validateVideoType(info, allowMoreVideoCodecs, onError);
  validatePixelFormat(videoFile, onError);
  validateAspectRatio(info, onError);
  validateDuration(info, onError);
  validateFileSize(info, onError);
  logSampleDuration(info, trackEventProperties);
  logTimescale(info, trackEventProperties);

  return {
    audioSampleRate,
    framerate,
    gopSeconds: isVariableFramerate ? null : gopSeconds,
    videoBitrateKbps,
    audioBitrateKbps,
    resolutionHeight: resolution.resolutionHeight,
  };
};

export default validateAndLogVideoFile;
